// src/composables/useNotifier.js
import { supabase } from '@/supabaseClient'

/**
 * Template lookup:
 * - Each entry can be an object { title, body } with {placeholders}
 *   OR a function (meta) => ({ title, body }) for full control.
 * - Placeholders in strings are filled from `meta` via {key}.
 */
const TEMPLATES = {
  // Generic system message (safe default)
  system_generic: {
    title: 'Notification',
    body: '{message}',
  },

  // Listing lifecycle examples
  listing_posted: {
    title: 'New Listing Submitted',
    body: 'An agent has created a new listing that requires approval',
  },
  listing_approved: {
    title: 'Listing approved',
    body: 'Your listing has been approved and is now live.',
  },
  listing_denied: {
    title: 'Listing denied',
    body: 'Your listing was denied. Reason: {reason}',
  },

  // Booking examples
  booking_requested: {
    title: 'New booking request',
    body: 'A guest requested to book. Please review.',
  },
  booking_approved: {
    title: 'Booking approved',
    body: 'A booking was approved. Complete next steps within 24 hours.',
  },
  booking_denied: {
    title: 'Booking denied',
    body: 'A booking request was denied.',
  },

  booking_confirmed: {
    title: 'Booking Confirmed',
    body: 'The agent confirmed space and uploaded the confirmation file',
  },
  booking_confirm_denied: {
    title: 'Booking Confirmed',
    body: 'The agent denied space for the booking',
  },

  approve_confirmation: {
    title: 'Confirmation Approved',
    body: 'Confirmation was accepted. Complete next steps within 24 hours.',
  },
  deny_confirmation: {
    title: 'Confirmation Denied',
    body: 'Confirmation was denied. Resubmit documentation within 24 hours',
  },

  payment_uploaded: {
    title: 'Payment Invoice uploaded',
    body: 'The agent uploaded invoice showing payment for booking #{bookingId}',
  },
  approve_payment: {
    title: 'Payment Invoice Approved',
    body: 'Payment Invoice was accepted. Complete next steps within 24 hours.',
  },
  deny_payment: {
    title: 'Payment Invoice Denied',
    body: 'Payment Invoice was denied. Resubmit documentation within 24 hours',
  },

  // Document workflow
  document_uploaded: {
    title: 'Document uploaded',
    body: 'New document uploaded for booking #{bookingId}.',
  },
  document_approved: {
    title: 'Document approved',
    body: 'Your document "{docName}" has been approved.',
  },
  document_denied: {
    title: 'Document denied',
    body: 'Your document "{docName}" was denied. Reason: {reason}',
  },

  // Chat/message example
  message_received: {
    title: 'New message',
    body: 'You received a new message about "{listingTitle}".',
  },

  // If you prefer dynamic titles/bodies, you can use a function:
  // custom_dynamic: (meta = {}) => ({
  //   title: `Hey ${meta.firstName || 'there'}!`,
  //   body: `We noticed activity on ${meta.listingTitle || 'your listing'}.`,
  // }),
}

const ADMIN_USER_ID = 'f9889116-3505-4377-9bb7-597800dadc83' // change if needed
const NOTIFICATIONS_TABLE = 'notifications'

function fillPlaceholders(str = '', meta = {}) {
  return String(str).replace(/\{(\w+)\}/g, (_, k) => {
    const v = meta?.[k]
    // show nothing if missing to avoid "undefined" in UI
    return v == null ? '' : String(v)
  })
}

function resolveTemplate(type, meta, overrides) {
  const tpl = TEMPLATES[type]
  if (!tpl) {
    throw new Error(`Unknown notification type: "${type}". Add it to TEMPLATES.`)
  }

  // Function template
  if (typeof tpl === 'function') {
    const res = tpl(meta || {})
    return {
      title: overrides?.title ?? res?.title ?? 'Notification',
      body: overrides?.body ?? res?.body ?? null,
    }
  }

  // Object template with placeholders
  return {
    title: overrides?.title ?? fillPlaceholders(tpl.title, meta),
    body: overrides?.body ?? (tpl.body ? fillPlaceholders(tpl.body, meta) : null),
  }
}

export function useNotifier() {
  /**
   * sendNotification({
   *   type: string (required, key in TEMPLATES),
   *   title?: string (overrides template),
   *   body?: string (overrides template),
   *   listingId?: string,
   *   meta?: object,      // used to fill {placeholders} in template
   *   userId?: string,    // defaults to current user
   *   forAdmin?: boolean  // if true, sends to ADMIN_USER_ID
   * })
   * Returns inserted notification id (uuid)
   */
  const sendNotification = async ({
    type = 'system_generic',
    title,
    body,
    listingId = null,
    meta = null,
    userId,
    forAdmin = false,
  } = {}) => {
    // Resolve target user
    if (forAdmin) {
      userId = ADMIN_USER_ID
    }
    if (!userId) {
      const { data: { user }, error: userErr } = await supabase.auth.getUser()
      if (userErr) throw userErr
      if (!user) throw new Error('sendNotification: no authenticated user')
      userId = user.id
    }

    // Resolve title/body from template (allow overrides)
    const { title: finalTitle, body: finalBody } = resolveTemplate(type, meta, { title, body })

    const payload = {
      user_id: userId,
      type,                   // keep the type key for filtering in UI
      title: String(finalTitle || 'Notification'),
      body: finalBody ?? null,
      listing_id: listingId ?? null,
      meta: meta ?? null,     // keep raw meta for the UI if you want
      // created_at defaulted by DB; read_at remains null
    }

    const { data, error } = await supabase
      .from(NOTIFICATIONS_TABLE)
      .insert([payload])
      .select('id')
      .single()

    if (error) throw error
    return data.id
  }

  /** mark a single notification as read (sets read_at = now()) */
  const markRead = async (id) => {
    const { error } = await supabase
      .from(NOTIFICATIONS_TABLE)
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
  }

  /** mark all current-user unread notifications as read */
  const markAllRead = async () => {
    const { data: { user }, error: userErr } = await supabase.auth.getUser()
    if (userErr) throw userErr
    if (!user) throw new Error('markAllRead: no authenticated user')

    const { error } = await supabase
      .from(NOTIFICATIONS_TABLE)
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .is('read_at', null)
    if (error) throw error
  }

  return { sendNotification, markRead, markAllRead }
}
